import React, { useState } from 'react';
import styled from 'styled-components';
import { loginWithEmail, loginWithGoogle } from '../services/authService';

const LoginContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  background: #f8fafc;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
  width: 100%;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    color: #64748b;
    background: rgba(0, 0, 0, 0.03);
  }

  &:active {
    color: #3b82f6;
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Button = styled.button<{ disabled?: boolean }>`
  padding: 14px 24px;
  background: ${props => props.disabled 
    ? '#e2e8f0' 
    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
  };
  color: ${props => props.disabled ? '#9ca3af' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface AuthLoginProps {
  onLogin: (teacher: any) => void;
}

const AuthLogin: React.FC<AuthLoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const user = await loginWithGoogle();
      if (user) {
        onLogin(user);
      }
    } catch (err: any) {
      setError(err.message || 'Google ilə daxil olarkən xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Email alanında @ yoksa, username olarak kabul et ve domain ekle
      let finalEmail = email.trim();
      if (!finalEmail.includes('@')) {
        finalEmail = `${finalEmail}@t.istedad21.edu.az`;
      }

      const user = await loginWithEmail(finalEmail, password);
      if (user) {
        onLogin(user);
      }
    } catch (err: any) {
      setError(err.message || 'Daxil olarkən xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Title>Daxil ol</Title>
      <Subtitle>İstifadəçi adı və ya email ilə daxil olun</Subtitle>
      
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="email">Email və ya İstifadəçi Adı</Label>
          <Input
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="İstifadəçi Adı"
            required
            disabled={loading}
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="password">Şifrə</Label>
          <PasswordInputWrapper>
            <PasswordInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
            <EyeButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              aria-label={showPassword ? 'Şifrəni gizlət' : 'Şifrəni göstər'}
            >
              {showPassword ? (
                // Göz açık (şifre görünür) - gizlemek için tıkla
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                // Göz kapalı (şifre gizli) - göstermek için tıkla
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </EyeButton>
          </PasswordInputWrapper>
        </InputGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button type="submit" disabled={loading}>
          {loading && <LoadingSpinner />}
          {loading ? 'Daxil olunur...' : 'Daxil ol'}
        </Button>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          margin: '12px 0' 
        }}>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>və ya</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>

        <Button 
          type="button" 
          disabled={loading}
          onClick={handleGoogleLogin}
          style={{ 
            background: 'white',
            color: '#374151',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ width: 18, height: 18 }}>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C34.4 31.9 30.6 35 26 35c-5.5 0-10-4.5-10-10s4.5-10 10-10c2.4 0 4.6.9 6.3 2.3l5.7-5.7C35.6 8.1 31 6 26 6 15.5 6 7 14.5 7 25s8.5 19 19 19 19-8.5 19-19c0-1.6-.2-3.1-.4-4.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.4 16.2 19 13 24 13c2.4 0 4.6.9 6.3 2.3l5.7-5.7C33.6 6.1 29 4 24 4 16.1 4 9.3 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13.1-5.1l-6-4.9C29.5 35.2 27 36 24 36c-4.5 0-8.3-3.1-9.6-7.3l-6.6 5C9.3 39.7 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.9-3.9 5.1l.1.1 6 4.9C39.1 35.4 41 30.5 41 25c0-1.6-.2-3.1-.4-4.5z"/>
          </svg>
          <span>Google ilə daxil ol</span>
        </Button>
      </Form>
    </LoginContainer>
  );
};

export default AuthLogin;

